/**
 * Formata um instante ISO (gravado em UTC pelo backend) no fuso do consultório.
 *
 * `new Date(iso).toLocaleString('pt-BR')` — o que era usado antes — converte para o fuso da *máquina
 * que renderiza*, não para o do consultório. No navegador da equipe isso acerta por coincidência,
 * mas o PDF que segue para assinatura é gerado por uma lambda headless cujo servidor roda em UTC:
 * o mesmo carimbo de assinatura saía 3h adiantado no documento em relação ao que a equipe viu na
 * tela. O locale 'pt-BR' controla só o formato (dd/mm/aaaa), nunca o fuso.
 *
 * Por isso o deslocamento vem do cadastro da empresa (`company.timezone`, em minutos — ex.: -180
 * para Brasília) e é congelado no contextSnapshot junto do resto do contexto, para tela e PDF
 * sempre concordarem.
 */

function pad(value: number): string {
    return String(value).padStart(2, '0')
}

function resolveOffsetMinutes(offsetMinutes?: number | null): number | null {
    if (offsetMinutes !== null && offsetMinutes !== undefined) return offsetMinutes
    const fromStore = typeof window !== 'undefined' ? window.store?.company?.timezone : null
    return fromStore ?? null
}

export function formatDateTime(iso: string | null | undefined, offsetMinutes?: number | null): string {
    if (!iso) return ''
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return ''

    const offset = resolveOffsetMinutes(offsetMinutes)
    // Sem fuso conhecido (ex.: link público antigo, sem snapshot), mantém o comportamento anterior:
    // melhor a hora da máquina que renderiza do que uma hora errada com aparência de certa.
    if (offset === null) return date.toLocaleString('pt-BR')

    // Desloca o instante e lê as partes em UTC — assim o resultado não depende do fuso do runtime.
    const shifted = new Date(date.getTime() + offset * 60000)
    const day = pad(shifted.getUTCDate())
    const month = pad(shifted.getUTCMonth() + 1)
    const year = shifted.getUTCFullYear()
    const hours = pad(shifted.getUTCHours())
    const minutes = pad(shifted.getUTCMinutes())
    const seconds = pad(shifted.getUTCSeconds())
    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`
}

/** Deslocamento a congelar no contextSnapshot; null quando não há empresa no contexto. */
export function currentCompanyOffsetMinutes(): number | null {
    return (typeof window !== 'undefined' ? window.store?.company?.timezone : null) ?? null
}

/**
 * Deslocamento para quando é obrigatório ter um valor: o do consultório quando há sessão, senão o do
 * próprio navegador (link público não tem `window.store`).
 *
 * `getTimezoneOffset()` devolve +180 para UTC-3 — sinal invertido em relação a `company.timezone`,
 * que guarda -180. Daí a negação.
 */
export function currentOffsetMinutes(): number {
    return currentCompanyOffsetMinutes() ?? -new Date().getTimezoneOffset()
}
